import { useCallback, useReducer } from 'react';
import type { RecurringGroupKey } from '@/lib/api';
import type { RecurringPaymentStatusView } from '@/lib/recurring/paymentStatus';
import type { RecurringCommitment } from './RecurringCommitmentRow';

export type GroupFilter = RecurringGroupKey | 'todos';
export type StatusFilter = RecurringPaymentStatusView | 'todas';

export interface CommitmentFilters {
  group: GroupFilter;
  status: StatusFilter;
  page: number;
}

type FilterAction =
  | { type: 'group'; value: GroupFilter }
  | { type: 'status'; value: StatusFilter }
  | { type: 'page'; value: number };

export const INITIAL_FILTERS: CommitmentFilters = { group: 'todos', status: 'todas', page: 1 };

/** Narrowing the list always returns to page 1 — page 4 of a 2-page result is an empty screen. */
export function commitmentFiltersReducer(
  state: CommitmentFilters,
  action: FilterAction,
): CommitmentFilters {
  switch (action.type) {
    case 'group':
      return { ...state, group: action.value, page: 1 };
    case 'status':
      return { ...state, status: action.value, page: 1 };
    case 'page':
      return { ...state, page: Math.max(1, action.value) };
  }
}

export function applyCommitmentFilters(
  commitments: readonly RecurringCommitment[],
  filters: CommitmentFilters,
): RecurringCommitment[] {
  return commitments.filter(
    (commitment) =>
      (filters.group === 'todos' || commitment.groupKey === filters.group) &&
      (filters.status === 'todas' || commitment.status === filters.status),
  );
}

export function useCommitmentFilters() {
  const [filters, dispatch] = useReducer(commitmentFiltersReducer, INITIAL_FILTERS);

  const selectGroup = useCallback((value: GroupFilter) => dispatch({ type: 'group', value }), []);
  const selectStatus = useCallback((value: StatusFilter) => dispatch({ type: 'status', value }), []);
  const goToPage = useCallback((value: number) => dispatch({ type: 'page', value }), []);

  return { filters, selectGroup, selectStatus, goToPage };
}
